import { useMemo } from 'react';

import useSWR from 'swr';

import { fetchConstellations } from '@src/utils/apis/LensApi';
import { CONSTELLATION_IDS_CONSTS } from '@src/utils/constants/LENS_CONSTANTS';

interface Collection {
  id: string;
  name: string;
  preview_url: string;
  rescale: [number, number];
  satellite_name: string;
}

const useConstellations = () => {
  const { isLoading, error, data } = useSWR(
    '/satellites',
    fetchConstellations,
    {
      onErrorRetry: error => {
        // Never retry on 404
        if (error.status === 404) return;
      }
    }
  );

  const constellations = useMemo(() => {
    return (
      data?.filter((d: { deprecated: boolean }) => d.deprecated !== true) ?? []
    );
  }, [data]);

  const collectionMap = useMemo(
    () =>
      constellations.reduce(
        (
          acc: { [x: string]: unknown },
          curr: { collections: { id: string }[]; name: string }
        ) => {
          curr.collections.forEach(d => {
            acc[d.id] = { ...d, satellite_name: curr.name };
          });

          return acc;
        },
        {}
      ),
    [constellations]
  ) as Record<string, Collection>;

  return {
    isLoading,
    error,
    constellations,
    collectionMap,
    satelliteMap: constellations?.reduce(
      (
        acc: Record<string, string>,
        { name, id }: { name: string; id: string }
      ) => {
        acc[id] = name;
        return acc;
      },
      {}
    ),
    sentinel2Data: constellations?.find((d: Collection) =>
      [CONSTELLATION_IDS_CONSTS.SENTINEL_2, 'Sentinel-2'].includes(d.id)
    )
  };
};

export default useConstellations;
